import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentData,
  FirestoreDataConverter,
  getDoc,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  SnapshotOptions,
  WithFieldValue,
} from 'firebase/firestore';
import { firestore } from '@/lib/firebase';
import {
  Channel,
  CHANNEL_GUIDE_COLLECTION,
  ChannelGuide,
} from '../schema/channelGuide';
import {
  useCollectionData,
  useDocumentData,
} from 'react-firebase-hooks/firestore';

// create a channel guide
export async function createChannelGuide(channelGuide: ChannelGuide) {
  const doc = await addDoc(collection(firestore, CHANNEL_GUIDE_COLLECTION), {
    ...channelGuide,
    createdAt: serverTimestamp(),
  });
  return doc.id;
}

// update a channel guide
export async function updateChannelGuide(
  id: string,
  channelGuide: Partial<ChannelGuide>
) {
  delete channelGuide.id;
  // Never let a client-side value overwrite the server creation timestamp.
  delete channelGuide.createdAt;

  await setDoc(
    doc(firestore, CHANNEL_GUIDE_COLLECTION, id),
    { ...channelGuide },
    {
      merge: true,
    }
  );
}

export async function cloneChannelGuide(channelGuideId: string) {
  const ref = doc(firestore, CHANNEL_GUIDE_COLLECTION, channelGuideId);

  const channelGuideSnapshot = await getDoc(ref);

  const channelGuideData = channelGuideSnapshot.data() as ChannelGuide;

  channelGuideData.name = `${channelGuideData.name} (Clone)`;

  await createChannelGuide(channelGuideData);
}

export async function updateChannelActivatedStatus(
  channelGuideId: string,
  channelId: string,
  activated: boolean
) {
  const ref = doc(firestore, CHANNEL_GUIDE_COLLECTION, channelGuideId);

  const channelGuideSnapshot = await getDoc(ref);

  const channelGuideData = channelGuideSnapshot.data() as ChannelGuide;

  const index = channelGuideData.channels.findIndex(
    (channel: Channel) => channel.id === channelId
  );

  if (index !== -1) {
    channelGuideData.channels[index] = {
      ...channelGuideData.channels[index],
      activated,
    };
  }
  await setDoc(ref, channelGuideData, { merge: true });
}

// hard delete a channel guide
export async function deleteChannelGuide(id: string) {
  await deleteDoc(doc(firestore, CHANNEL_GUIDE_COLLECTION, id));
}

export async function deleteChannel(channelGuideId: string, channelId: string) {
  const ref = doc(firestore, CHANNEL_GUIDE_COLLECTION, channelGuideId);

  const channelGuideSnapshot = await getDoc(ref);

  const channelGuideData = channelGuideSnapshot.data() as ChannelGuide;
  const updatedChannels = channelGuideData.channels.filter(
    (channel) => channel.id !== channelId
  );

  await setDoc(ref, { channels: updatedChannels }, { merge: true });
}

const taskExecutionConverter: FirestoreDataConverter<ChannelGuide> = {
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions
  ): any {
    const data = snapshot.data(options);
    return {
      ...data,
      id: snapshot.id,
      createdAt: data.createdAt?.toDate?.() ?? null,
    } as ChannelGuide;
  },
  toFirestore: function (): WithFieldValue<DocumentData> {
    throw new Error('Function not implemented.');
  },
};

// list all channel guides
export function useChannelGuides() {
  return useCollectionData(
    collection(firestore, CHANNEL_GUIDE_COLLECTION).withConverter(
      taskExecutionConverter
    ),
    {
      snapshotListenOptions: { includeMetadataChanges: false },
    }
  );
}

export function useChannelGuide(id?: string): any {
  // Guard: doc() throws on an undefined segment, which would crash the page.
  return useDocumentData(
    id
      ? doc(firestore, CHANNEL_GUIDE_COLLECTION, id).withConverter(
          taskExecutionConverter
        )
      : undefined,
    {
      snapshotListenOptions: { includeMetadataChanges: false },
    }
  );
}
